import { JOB_TITLES } from '@account/constants';
import BriefcaseIcon from '@assets/icons/briefcase.svg?react';
import BuildingIcon from '@assets/icons/building.svg?react';
import { SelectOption } from '@common/types/common';
import { AuthMeUser, Roles } from '@common/types/user';
import { GradientCircularProgress } from '@components/display/loading';
import { useAuth } from '@hooks/useAuth';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { Avatar, Box, CircularProgress, Divider, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { useGetCountriesQuery } from '@services/countries';
import { useGetUserCredentialTypesQuery } from '@services/keycloak';
import { useGetRolesQuery } from '@services/userRoles';
import { stringAvatar } from '@utils/common';
import { FOOD_PREFERENCES, getRoleName } from '@utils/user';
import { useMemo } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

type InformationProps = {
    user?: AuthMeUser;
    roles?: SelectOption<Roles>[];
    countries?: SelectOption[];
    jobTitles: SelectOption[];
    foodPreferences: SelectOption[];
};

const getInformations = ({ user, roles, countries, jobTitles, foodPreferences }: InformationProps, intl: IntlShape) => {
    const notFilled = <FormattedMessage id="common.notFilled" defaultMessage="Not Filled" />;
    const notSelected = <FormattedMessage id="common.notSelected" defaultMessage="Not Selected" />;
    const unknown = <FormattedMessage id="common.unknown" defaultMessage="Unknown" />;

    return [
        {
            label: <FormattedMessage id="common.name" defaultMessage="Name" />,
            icon: <PersonOutlineIcon fontSize="small" />,
            value: !user?.first_name && !user?.last_name ? notFilled : `${user?.first_name} ${user?.last_name}`,
        },
        {
            label: <FormattedMessage id="common.email" defaultMessage="Email" />,
            icon: <EmailOutlinedIcon fontSize="small" />,
            value: !user?.email ? notFilled : user.email,
        },
        {
            label: <FormattedMessage id="common.role" defaultMessage="Role" />,
            icon: <BriefcaseIcon />,
            value: (() => {
                const role = roles?.find((r) => r.value === user?.role);
                return role ? getRoleName(role.value, intl) : notSelected;
            })(),
        },
        {
            label: <FormattedMessage id="common.mobilePhoneNumber" defaultMessage="Mobile Phone Number" />,
            icon: <PhoneIphoneIcon fontSize="small" />,
            value: !user?.mobile_phone ? notFilled : user.mobile_phone,
        },
        {
            label: <FormattedMessage id="common.telephoneNumber" defaultMessage="Telephone Number" />,
            icon: <CallOutlinedIcon fontSize="small" />,
            value: !user?.telephone ? notFilled : user.telephone,
        },
        {
            label: <FormattedMessage id="common.faxNumber" defaultMessage="Fax Number" />,
            icon: <PrintOutlinedIcon fontSize="small" />,
            value: !user?.fax ? notFilled : user.fax,
        },
        {
            label: <FormattedMessage id="common.company" defaultMessage="Company" />,
            icon: <BuildingIcon />,
            value: !user?.company_name ? notFilled : user.company_name,
        },
        {
            label: <FormattedMessage id="common.jobTitle" defaultMessage="Job Title" />,
            icon: <VerifiedOutlinedIcon fontSize="small" />,
            value: !user?.job_title
                ? notSelected
                : user.job_title === 'other'
                ? user?.job_title_other || notFilled
                : jobTitles.find((title) => title.value === user?.job_title)?.label || notSelected,
        },
        {
            label: <FormattedMessage id="common.country" defaultMessage="Country" />,
            icon: <LanguageIcon fontSize="small" />,
            value:
                !user?.country_id || !countries
                    ? notSelected
                    : countries.find((country) => country.value === user?.country_id)?.label || notSelected,
        },
        {
            label: <FormattedMessage id="common.city" defaultMessage="City" />,
            icon: <PlaceOutlinedIcon fontSize="small" />,
            value: !user?.city ? notFilled : user.city,
        },
        {
            label: <FormattedMessage id="common.foodPreference" defaultMessage="Food Preference" />,
            icon: <RestaurantIcon fontSize="small" />,
            value: !user?.food_preference
                ? unknown
                : foodPreferences.find((pref) => pref.value === user?.food_preference)?.label ?? unknown,
        },
    ];
};

export const AccountOverview = () => {
    //#region Data Fetching
    const { user, isUserLoading } = useAuth();
    const { data: roles, isLoading: isRolesLoading } = useGetRolesQuery();
    const { data: countries, isLoading: isCountriesLoading } = useGetCountriesQuery();
    const { data: userCredentialTypes, isLoading: isUserCredentialTypesLoading } = useGetUserCredentialTypesQuery();
    //#endregion

    //#region Hooks
    const intl = useIntl();
    const navigate = useNavigate();
    //#endregion

    //#region Processed Data
    const loading = isUserLoading || isRolesLoading || isCountriesLoading || isUserCredentialTypesLoading;
    const accountProgress = useMemo(
        () => [
            {
                isComplete: !!(user?.first_name && user.last_name),
                label: loading ? (
                    <Skeleton width={100} />
                ) : !!(user?.first_name && user.last_name) ? (
                    <FormattedMessage id="account.overview.nameAdded" defaultMessage="Name Added" />
                ) : (
                    <FormattedMessage id="account.overview.addName" defaultMessage="Add Name" />
                ),
                onClick: () =>
                    navigate('edit', {
                        state: {
                            focusField: 'first_name',
                        },
                    }),
            },
            {
                isComplete: !!user?.email,
                label: loading ? (
                    <Skeleton width={100} />
                ) : !!user?.email ? (
                    <FormattedMessage id="account.overview.emailAdded" defaultMessage="Email Added" />
                ) : (
                    <FormattedMessage id="account.overview.addEmail" defaultMessage="Add Email" />
                ),
                onClick: () =>
                    navigate('edit', {
                        state: {
                            focusField: 'email',
                        },
                    }),
            },
            {
                isComplete: !!user?.role,
                label: loading ? (
                    <Skeleton width={100} />
                ) : !!user?.role ? (
                    <FormattedMessage id="account.overview.roleAdded" defaultMessage="Role Added" />
                ) : (
                    <FormattedMessage id="account.overview.addRole" defaultMessage="Add Role" />
                ),
                onClick: () =>
                    navigate('edit', {
                        state: {
                            focusField: 'role',
                        },
                    }),
            },
            {
                isComplete: !!user?.country_id,
                label: loading ? (
                    <Skeleton width={120} />
                ) : !!user?.country_id ? (
                    <FormattedMessage id="account.overview.countryAdded" defaultMessage="Country Added" />
                ) : (
                    <FormattedMessage id="account.overview.addCountry" defaultMessage="Add Country" />
                ),
                onClick: () =>
                    navigate('edit', {
                        state: {
                            focusField: 'country_code',
                        },
                    }),
            },
            {
                isComplete: !!user?.city,
                label: loading ? (
                    <Skeleton width={100} />
                ) : !!user?.city ? (
                    <FormattedMessage id="account.overview.cityAdded" defaultMessage="City Added" />
                ) : (
                    <FormattedMessage id="account.overview.addCity" defaultMessage="Add City" />
                ),
                onClick: () =>
                    navigate('edit', {
                        state: {
                            focusField: 'city',
                        },
                    }),
            },
            {
                isComplete: !!user?.language,
                label: loading ? (
                    <Skeleton width={140} />
                ) : !!user?.language ? (
                    <FormattedMessage id="account.overview.languageAdded" defaultMessage="Language Added" />
                ) : (
                    <FormattedMessage id="account.overview.addLanguage" defaultMessage="Add Language" />
                ),
                onClick: () =>
                    navigate('edit', {
                        state: {
                            focusField: 'language',
                        },
                    }),
            },
            ...(!loading && user?.uses_keycloak
                ? [
                      {
                          isComplete:
                              !!userCredentialTypes?.length && userCredentialTypes.some((cred) => cred.type === 'otp'),
                          label:
                              !!userCredentialTypes?.length &&
                              userCredentialTypes.some((cred) => cred.type === 'otp') ? (
                                  <FormattedMessage id="account.overview.2FAEnabled" defaultMessage="2FA Enabled" />
                              ) : (
                                  <FormattedMessage id="account.overview.enable2FA" defaultMessage="Enable 2FA" />
                              ),
                          onClick: () =>
                              window.open(
                                  `${import.meta.env.VITE_KEYCLOAK_URL}/account/account-security/signing-in`,
                                  '_blank'
                              ),
                      },
                  ]
                : []),
        ],
        [user, userCredentialTypes, loading]
    );
    const jobTitleOptions = JOB_TITLES(intl);
    const foodPreferenceOptions = FOOD_PREFERENCES(intl);
    const informations = useMemo(
        () =>
            getInformations(
                { jobTitles: jobTitleOptions, foodPreferences: foodPreferenceOptions, user, roles, countries },
                intl
            ),
        [jobTitleOptions, foodPreferenceOptions, user, roles, countries]
    );
    //#endregion

    return (
        <>
            <title>Your account details - Victron Professional</title>
            <Stack direction={{ xs: 'column-reverse', lg: 'row' }} gap="24px">
                <Box bgcolor="#fff" className="black-border" borderRadius="5px" p="24px" flex={1}>
                    <Typography fontFamily="museo-sans" fontSize="26px" letterSpacing="-1px" py="25px">
                        <FormattedMessage id="account.overview.title" defaultMessage="Your Information" />
                    </Typography>
                    <Divider className="mb-[24px]" />
                    <Stack direction="column" divider={<Divider />}>
                        {informations.map((info) => (
                            <Stack
                                key={`info-${info.label}`}
                                direction="row"
                                py="14px"
                                px="10px"
                                alignItems="center"
                                justifyContent="space-between"
                                bgcolor="neutral-100.main"
                                gap="5px"
                            >
                                <Stack direction="row" alignItems="center" gap="5.5px">
                                    {info.icon}
                                    <Typography fontSize="13px" fontWeight={500}>
                                        {info.label}
                                    </Typography>
                                </Stack>
                                <Typography
                                    fontSize="13px"
                                    color="neutral-800"
                                    textOverflow="ellipsis"
                                    whiteSpace="nowrap"
                                    overflow="hidden"
                                >
                                    {loading ? <Skeleton width={100} /> : info.value}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Box>
                <Box
                    bgcolor="#fff"
                    className="black-border"
                    borderRadius="5px"
                    py="28px"
                    px="16px"
                    width={{ xs: '100%', lg: 300 }}
                >
                    <Box mx="auto" width="fit-content" position="relative">
                        <GradientCircularProgress
                            size={135}
                            value={
                                loading
                                    ? 0
                                    : Math.round(
                                          (accountProgress.reduce(
                                              (acc, progress) => acc + (progress.isComplete ? 1 : 0),
                                              0
                                          ) *
                                              100) /
                                              accountProgress.length
                                      ) || 0
                            }
                            thickness={2}
                            showProgress={!loading}
                        />
                        {loading ? (
                            <CircularProgress
                                className="absolute top-0 left-0 right-0 bottom-[5px] m-auto"
                                size={113}
                            />
                        ) : (
                            <Avatar
                                {...stringAvatar(
                                    user?.first_name && user?.last_name ? `${user?.first_name} ${user?.last_name}` : '',
                                    {
                                        fontSize: 42,
                                    },
                                    '',
                                    true
                                )}
                                variant="circular"
                                className="w-[113px] h-[113px] absolute top-0 left-0 right-0 bottom-[5px] m-auto"
                            />
                        )}
                    </Box>
                    <Typography
                        textAlign="center"
                        fontFamily="museo-sans"
                        fontSize={20}
                        letterSpacing={-0.621}
                        mt="10px"
                    >
                        <FormattedMessage id="account.overview" defaultMessage="Account Overview" />
                    </Typography>
                    <Typography textAlign="center" fontSize={16} color="neutral-800" mb="50px">
                        <FormattedMessage
                            id="account.completionProgress"
                            defaultMessage="Account completion progress"
                        />
                    </Typography>
                    <Stack direction="column" gap="25px" pl="6px" pb="4.3px">
                        {accountProgress.map((progress, index) => (
                            <Stack key={`progress-${index}`} direction="row" gap="8px" alignItems="center">
                                {loading ? (
                                    <CircularProgress size={20} />
                                ) : progress.isComplete ? (
                                    <CheckCircleIcon color="primary" />
                                ) : (
                                    <IconButton className="p-0" onClick={progress.onClick}>
                                        <AddCircleOutlineIcon color="primary" />{' '}
                                    </IconButton>
                                )}
                                <Typography fontFamily="museo-sans" fontSize={16} color="neutral-900" fontWeight={600}>
                                    {progress.label}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Box>
            </Stack>
        </>
    );
};
